<script>
  import { Camera, LoaderCircle, Radio, RefreshCw, Settings2, Square, SwitchCamera } from 'lucide-svelte';

  import CaptureThumbnail from './CaptureThumbnail.svelte';

  export let recording = false;
  export let stopping = false;
  export let pendingChunks = 0;
  export let showSync = true;
  export let latestCapture = null;
  export let captureCount = 0;
  export let onCapture = () => {};
  export let onStart = () => {};
  export let onStop = () => {};
  export let onSwitch = () => {};
  export let onSync = () => {};
  export let onOpenGallery = () => {};
  export let onOpenSettings = () => {};
</script>

<div class="controls-wrap">
  <div class="glass-dock">
    <div class="side-actions">
      <CaptureThumbnail capture={latestCapture} count={captureCount} onOpen={onOpenGallery} />

      <button
        type="button"
        class="control-button"
        aria-label="Mở cài đặt camera"
        title="Mở cài đặt camera"
        on:click={onOpenSettings}>
        <Settings2 size="21" />
        <span>Cài đặt</span>
      </button>

      <button
        type="button"
        class="control-button"
        aria-label="Chuyển camera"
        title="Chuyển camera"
        disabled={recording || stopping}
        on:click={onSwitch}>
        <SwitchCamera size="21" />
        <span>Đổi camera</span>
      </button>
    </div>

    <button
      type="button"
      class="shutter-button"
      aria-label="Chụp ảnh"
      title="Chụp ảnh"
      disabled={stopping}
      on:click={onCapture}>
      <span class="shutter-inner"><Camera size="24" /></span>
    </button>

    <div class="side-actions right">
      <button
        type="button"
        class:recording
        class="record-button"
        aria-label={recording ? 'Dừng ghi' : 'Ghi'}
        title={recording ? 'Dừng ghi' : 'Ghi'}
        disabled={stopping}
        on:click={recording ? onStop : onStart}>
        {#if stopping}
          <span class="spin"><LoaderCircle size="22" /></span>
        {:else if recording}
          <Square size="19" fill="currentColor" />
        {:else}
          <Radio size="22" />
        {/if}
        <span>{stopping ? 'Đang lưu' : recording ? 'Dừng ghi' : 'Ghi'}</span>
      </button>

      {#if showSync}
        <button
          type="button"
          class="control-button"
          aria-label="Đồng bộ lại"
          title="Đồng bộ lại"
          on:click={onSync}>
          <RefreshCw size="20" />
          <span>Đồng bộ{pendingChunks ? ` (${pendingChunks})` : ''}</span>
        </button>
      {/if}
    </div>
  </div>
</div>

<style>
  .controls-wrap {
    position: absolute;
    z-index: 70;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    padding: 0 0.75rem max(0.8rem, env(safe-area-inset-bottom));
    pointer-events: none;
  }

  .glass-dock {
    pointer-events: auto;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: center;
    gap: 0.65rem;
    width: min(100%, 45rem);
    min-height: 5.2rem;
    padding: 0.65rem 0.7rem;
    border: 1px solid rgba(255, 255, 255, 0.22);
    border-radius: 2rem;
    color: white;
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.2), rgba(2, 6, 23, 0.38));
    box-shadow:
      0 18px 50px rgba(0, 0, 0, 0.34),
      inset 0 1px 0 rgba(255, 255, 255, 0.28);
    backdrop-filter: blur(28px) saturate(175%);
    -webkit-backdrop-filter: blur(28px) saturate(175%);
  }

  .side-actions {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    min-width: 0;
  }

  .side-actions.right {
    justify-content: flex-end;
  }

  .control-button,
  .record-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    min-width: 2.7rem;
    min-height: 2.7rem;
    padding: 0.55rem;
    border: 1px solid rgba(255, 255, 255, 0.16);
    border-radius: 999px;
    color: white;
    background: rgba(255, 255, 255, 0.12);
    transition: transform 160ms ease, background 160ms ease, opacity 160ms ease;
  }

  .control-button:hover,
  .record-button:hover {
    transform: translateY(-1px);
    background: rgba(255, 255, 255, 0.22);
  }

  .control-button:disabled,
  .record-button:disabled,
  .shutter-button:disabled {
    opacity: 0.42;
  }

  .control-button span,
  .record-button span {
    display: none;
  }

  .record-button {
    color: #fecaca;
    background: rgba(239, 68, 68, 0.2);
  }

  .record-button.recording {
    color: white;
    background: rgba(239, 68, 68, 0.72);
  }

  .shutter-button {
    display: grid;
    place-items: center;
    width: 4.15rem;
    height: 4.15rem;
    padding: 0.25rem;
    border: 2px solid rgba(255, 255, 255, 0.9);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.16);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.28);
    transition: transform 140ms ease;
  }

  .shutter-button:active {
    transform: scale(0.94);
  }

  .shutter-inner {
    display: grid;
    place-items: center;
    width: 100%;
    height: 100%;
    border-radius: inherit;
    color: #0f172a;
    background: rgba(255, 255, 255, 0.94);
  }

  .spin {
    animation: spin 0.9s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (min-width: 768px) {
    .controls-wrap {
      padding-bottom: 1.1rem;
    }

    .glass-dock {
      gap: 1rem;
      min-height: 5.6rem;
      padding: 0.8rem 1rem;
      border-radius: 2.25rem;
    }

    .control-button,
    .record-button {
      min-height: 2.9rem;
      padding: 0.6rem 0.85rem;
      font-size: 0.75rem;
      font-weight: 650;
    }

    .control-button span,
    .record-button span {
      display: inline;
      white-space: nowrap;
    }

    .shutter-button {
      width: 4.6rem;
      height: 4.6rem;
    }
  }
</style>
